/**
 * TrainingDataPage — 训练数据管理中心
 *
 * Two tabs:
 * 1. Training Examples — Skill 训练样本 CRUD (golden examples, quality scores, tags)
 * 2. Embeddings — pgvector embedding management (pending, batch, search)
 *
 * API (Examples):
 *   GET    /training/skills/:skillId/examples
 *   GET    /training/skills/:skillId/examples/few-shot
 *   PUT    /training/skills/:skillId/examples/:key
 *   DELETE /training/skills/:skillId/examples/:key
 *
 * API (Embeddings):
 *   GET    /training/embeddings/pending
 *   PUT    /training/embeddings/:exampleId
 *   POST   /training/embeddings/batch
 *   POST   /training/embeddings/search
 *
 * API (Preferences):
 *   GET    /training/customers/:customerId/preferences
 *   PUT    /training/customers/:customerId/preferences/:cat/:key
 *   DELETE /training/customers/:customerId/preferences/:cat/:key
 */

import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import {
  BookOpen,
  Search,
  Loader2,
  Plus,
  Trash2,
  Star,
  CheckCircle,
  XCircle,
  Clock,
  Cpu,
  RefreshCw,
  Database,
  AlertCircle,
  Heart,
  Tag,
  Save,
  Eye,
  ChevronRight,
} from 'lucide-react';

// ─── Types ─────────────────────────────────────────

interface TrainingExample {
  id: string;
  skill_id: string;
  example_key: string;
  input: unknown;
  expected_output: unknown;
  actual_output: unknown | null;
  description: string | null;
  tags: string[];
  quality_score: number | null;
  is_golden: boolean;
  validation_status: 'pending' | 'approved' | 'rejected';
  source: string | null;
  use_count: number;
  created_at: string;
}

interface PendingEmbedding {
  id: string;
  skill_id: string;
  example_key: string;
  description: string | null;
}

interface CustomerPreference {
  id: string;
  customer_id: string;
  category: string;
  preference_key: string;
  value: unknown;
  source: string;
  confidence: number;
  updated_at: string;
}

type TabId = 'examples' | 'embeddings' | 'preferences';

// ─── Component ─────────────────────────────────────

export default function TrainingDataPage() {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<TabId>('examples');
  const [skillFilter, setSkillFilter] = useState('email-parsing');
  const [searchQuery, setSearchQuery] = useState('');

  // Examples state
  const [examples, setExamples] = useState<TrainingExample[]>([]);
  const [examplesLoading, setExamplesLoading] = useState(false);
  const [expandedExample, setExpandedExample] = useState<string | null>(null);

  // Embeddings state
  const [pendingEmbeddings, setPendingEmbeddings] = useState<PendingEmbedding[]>([]);
  const [embeddingsLoading, setEmbeddingsLoading] = useState(false);

  // Preferences state
  const [customerIdInput, setCustomerIdInput] = useState('');
  const [preferences, setPreferences] = useState<CustomerPreference[]>([]);
  const [prefsLoading, setPrefsLoading] = useState(false);

  // New example form
  const [showNewForm, setShowNewForm] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newInput, setNewInput] = useState('');
  const [newOutput, setNewOutput] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [saving, setSaving] = useState(false);

  // ─── Load examples ───────────────────────────────

  const loadExamples = useCallback(async () => {
    setExamplesLoading(true);
    try {
      const data = await api.get<TrainingExample[]>(
        `/training/skills/${skillFilter}/examples?limit=50`,
      );
      setExamples(data);
    } catch {
      setExamples([]);
    } finally {
      setExamplesLoading(false);
    }
  }, [skillFilter]);

  useEffect(() => {
    if (activeTab === 'examples') loadExamples();
  }, [activeTab, loadExamples]);

  // ─── Load pending embeddings ─────────────────────

  const loadPending = useCallback(async () => {
    setEmbeddingsLoading(true);
    try {
      const data = await api.get<PendingEmbedding[]>(
        `/training/embeddings/pending?limit=100`,
      );
      setPendingEmbeddings(data);
    } finally {
      setEmbeddingsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'embeddings') loadPending();
  }, [activeTab, loadPending]);

  // ─── Load preferences ────────────────────────────

  const loadPreferences = async () => {
    if (!customerIdInput.trim()) return;
    setPrefsLoading(true);
    try {
      const data = await api.get<CustomerPreference[]>(
        `/training/customers/${customerIdInput}/preferences`,
      );
      setPreferences(data);
    } catch {
      setPreferences([]);
    } finally {
      setPrefsLoading(false);
    }
  };

  // ─── Save new example ────────────────────────────

  const handleSaveExample = async () => {
    if (!newKey || !newInput || !newOutput) return;
    setSaving(true);
    try {
      let inputParsed: unknown;
      let outputParsed: unknown;
      try { inputParsed = JSON.parse(newInput); } catch { inputParsed = newInput; }
      try { outputParsed = JSON.parse(newOutput); } catch { outputParsed = newOutput; }

      await api.post(`/training/skills/${skillFilter}/examples/${newKey}`, {
        input: inputParsed,
        expectedOutput: outputParsed,
        description: newDescription || undefined,
        validationStatus: 'pending',
        source: 'manual',
      });
      setShowNewForm(false);
      setNewKey('');
      setNewInput('');
      setNewOutput('');
      setNewDescription('');
      await loadExamples();
    } finally {
      setSaving(false);
    }
  };

  // ─── Delete example ──────────────────────────────

  const handleDeleteExample = async (key: string) => {
    setSaving(true);
    try {
      await api.delete(`/training/skills/${skillFilter}/examples/${key}`);
      await loadExamples();
    } finally {
      setSaving(false);
    }
  };

  // ─── Delete preference ───────────────────────────

  const handleDeletePref = async (customerId: string, cat: string, key: string) => {
    try {
      await api.delete(`/training/customers/${customerId}/preferences/${cat}/${key}`);
      await loadPreferences();
    } catch { /* ignore */ }
  };

  // ─── Trigger batch embedding ─────────────────────

  const triggerBatchEmbedding = async () => {
    // This would normally call an internal endpoint that generates embeddings
    // For now, just refresh the pending list
    await loadPending();
  };

  // Skills for filter
  const SKILLS = [
    'email-parsing', 'job-creation', 'job-validation', 'hs-code',
    'billing', 'customer-inquiry', 'sea-import', 'nvocc',
    'dg-validation', 'compliance', 'container-trucking',
  ];

  const filteredExamples = examples.filter(
    (e) =>
      !searchQuery ||
      e.example_key.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (e.description ?? '').toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // ─── Render ──────────────────────────────────────

  return (
    <div className="flex flex-col h-full p-4 gap-4">
      {/* Header + tabs */}
      <Card>
        <CardContent className="py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <BookOpen className="h-5 w-5 text-primary" />
            <h1 className="font-semibold">{t('admin.training.title')}</h1>
          </div>
          <div className="flex gap-1">
            {([
              { id: 'examples' as TabId, label: t('admin.training.tabExamples'), icon: BookOpen },
              { id: 'embeddings' as TabId, label: t('admin.training.tabEmbeddings'), icon: Cpu },
              { id: 'preferences' as TabId, label: t('admin.training.tabPreferences'), icon: Heart },
            ]).map(({ id, label, icon: Icon }) => (
              <Button
                key={id}
                variant={activeTab === id ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setActiveTab(id)}
              >
                <Icon className="h-3.5 w-3.5 mr-1" />
                {label}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Tab: Training Examples */}
      {activeTab === 'examples' && (
        <div className="flex-1 flex flex-col gap-3 min-h-0">
          {/* Toolbar */}
          <div className="flex items-center gap-2">
            <select
              value={skillFilter}
              onChange={(e) => setSkillFilter(e.target.value)}
              className="h-8 rounded border text-xs px-2 bg-background"
            >
              {SKILLS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder={t('admin.training.searchExamples')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 text-xs"
              />
            </div>
            <Button variant="outline" size="sm" onClick={loadExamples}>
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              {t('admin.refresh')}
            </Button>
            <Button size="sm" onClick={() => setShowNewForm(true)}>
              <Plus className="h-3.5 w-3.5 mr-1" />
              {t('admin.training.newExample')}
            </Button>
          </div>

          {/* Stats */}
          <div className="flex gap-3">
            <Badge variant="outline" className="text-xs">
              {t('admin.training.total', { count: examples.length })}
            </Badge>
            <Badge className="bg-yellow-500/10 text-yellow-600 text-xs">
              <Star className="h-3 w-3 mr-1" />
              {t('admin.training.golden')}: {examples.filter((e) => e.is_golden).length}
            </Badge>
            <Badge className="bg-green-500/10 text-green-600 text-xs">
              <CheckCircle className="h-3 w-3 mr-1" />
              {t('admin.training.approved')}: {examples.filter((e) => e.validation_status === 'approved').length}
            </Badge>
            <Badge className="bg-amber-500/10 text-amber-600 text-xs">
              <Clock className="h-3 w-3 mr-1" />
              {t('admin.training.pending')}: {examples.filter((e) => e.validation_status === 'pending').length}
            </Badge>
          </div>

          {/* New example form */}
          {showNewForm && (
            <Card className="border-primary/30">
              <CardContent className="py-3 space-y-2">
                <div className="flex items-center gap-2">
                  <Input
                    value={newKey}
                    onChange={(e) => setNewKey(e.target.value)}
                    placeholder={t('admin.training.sampleKey')}
                    className="h-8 text-xs flex-1"
                  />
                  <Input
                    value={newDescription}
                    onChange={(e) => setNewDescription(e.target.value)}
                    placeholder={t('admin.training.description')}
                    className="h-8 text-xs flex-1"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-muted-foreground">{t('admin.training.inputLabel')}</label>
                    <textarea
                      value={newInput}
                      onChange={(e) => setNewInput(e.target.value)}
                      className="w-full h-24 rounded border bg-muted/30 p-2 text-xs font-mono resize-none"
                      placeholder='{"email_body": "..."}'
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-muted-foreground">{t('admin.training.outputLabel')}</label>
                    <textarea
                      value={newOutput}
                      onChange={(e) => setNewOutput(e.target.value)}
                      className="w-full h-24 rounded border bg-muted/30 p-2 text-xs font-mono resize-none"
                      placeholder='{"job_type": "IMP", ...}'
                    />
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="ghost" size="sm" onClick={() => setShowNewForm(false)}>{t('admin.cancel')}</Button>
                  <Button size="sm" onClick={handleSaveExample} disabled={saving || !newKey || !newInput || !newOutput}>
                    {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                    {t('admin.save')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Examples list */}
          <div className="flex-1 overflow-auto space-y-2">
            {examplesLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredExamples.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Database className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">{t('admin.training.noExamples')}</p>
              </div>
            ) : (
              filteredExamples.map((ex) => (
                <Card key={ex.id} className={cn(ex.is_golden && 'border-yellow-500/30')}>
                  <CardContent className="py-2 px-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button onClick={() => setExpandedExample(expandedExample === ex.id ? null : ex.id)}>
                          <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', expandedExample === ex.id && 'rotate-90')} />
                        </button>
                        <span className="text-xs font-mono font-medium">{ex.example_key}</span>
                        {ex.is_golden && (
                          <Badge className="bg-yellow-500/10 text-yellow-600 text-[10px]">
                            <Star className="h-3 w-3 mr-0.5" />
                            Golden
                          </Badge>
                        )}
                        <Badge
                          variant="outline"
                          className={cn(
                            'text-[10px]',
                            ex.validation_status === 'approved' && 'text-green-600',
                            ex.validation_status === 'pending' && 'text-amber-600',
                            ex.validation_status === 'rejected' && 'text-red-600',
                          )}
                        >
                          {ex.validation_status === 'approved' ? t('admin.training.approved') : ex.validation_status === 'pending' ? t('admin.training.pending') : t('admin.training.rejected')}
                        </Badge>
                        {ex.quality_score != null && (
                          <span className="text-[10px] text-muted-foreground">
                            {t('admin.training.qualityLabel')}: {(ex.quality_score * 100).toFixed(0)}%
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {ex.tags?.map((tag) => (
                          <Badge key={tag} variant="outline" className="text-[10px]">
                            <Tag className="h-2.5 w-2.5 mr-0.5" />
                            {tag}
                          </Badge>
                        ))}
                        <span className="text-[10px] text-muted-foreground">
                          Used {ex.use_count}x
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-red-500"
                          onClick={() => handleDeleteExample(ex.example_key)}
                          disabled={saving}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    {ex.description && (
                      <p className="text-xs text-muted-foreground mt-1 pl-5">{ex.description}</p>
                    )}
                    {expandedExample === ex.id && (
                      <div className="mt-2 pl-5 grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[10px] text-muted-foreground">Input</label>
                          <pre className="text-xs bg-muted/30 rounded p-2 max-h-40 overflow-auto font-mono">
                            {typeof ex.input === 'string' ? ex.input : JSON.stringify(ex.input, null, 2)}
                          </pre>
                        </div>
                        <div>
                          <label className="text-[10px] text-muted-foreground">Expected Output</label>
                          <pre className="text-xs bg-muted/30 rounded p-2 max-h-40 overflow-auto font-mono">
                            {typeof ex.expected_output === 'string' ? ex.expected_output : JSON.stringify(ex.expected_output, null, 2)}
                          </pre>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      )}

      {/* Tab: Embeddings */}
      {activeTab === 'embeddings' && (
        <div className="flex-1 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={loadPending}>
              <RefreshCw className="h-3.5 w-3.5 mr-1" />
              Refresh
            </Button>
            <Button size="sm" onClick={triggerBatchEmbedding} disabled={pendingEmbeddings.length === 0}>
              <Cpu className="h-3.5 w-3.5 mr-1" />
              Batch Generate Embeddings ({pendingEmbeddings.length} pending)
            </Button>
          </div>

          <Card className="flex-1 overflow-auto">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Cpu className="h-4 w-4" />
                Pending Embeddings ({pendingEmbeddings.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {embeddingsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : pendingEmbeddings.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <CheckCircle className="h-8 w-8 mx-auto mb-2 text-green-500 opacity-50" />
                  <p className="text-sm">All samples have embeddings</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {pendingEmbeddings.map((pe) => (
                    <div key={pe.id} className="flex items-center gap-3 rounded border p-2 text-xs">
                      <AlertCircle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                      <span className="font-mono">{pe.example_key}</span>
                      <Badge variant="outline" className="text-[10px]">{pe.skill_id}</Badge>
                      {pe.description && (
                        <span className="text-muted-foreground truncate">{pe.description}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tab: Customer Preferences */}
      {activeTab === 'preferences' && (
        <div className="flex-1 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <Input
              value={customerIdInput}
              onChange={(e) => setCustomerIdInput(e.target.value)}
              placeholder="Enter customer ID..."
              className="h-8 text-xs max-w-xs"
            />
            <Button size="sm" onClick={loadPreferences} disabled={!customerIdInput.trim()}>
              <Search className="h-3.5 w-3.5 mr-1" />
              Search
            </Button>
          </div>

          <Card className="flex-1 overflow-auto">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Heart className="h-4 w-4" />
                Customer Preferences ({preferences.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {prefsLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                </div>
              ) : preferences.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  {customerIdInput ? 'No preferences found for this customer' : 'Enter a customer ID to search'}
                </p>
              ) : (
                <div className="space-y-1">
                  {preferences.map((p) => (
                    <div key={p.id} className="flex items-center justify-between rounded border p-2 text-xs">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">{p.category}</Badge>
                        <span className="font-mono">{p.preference_key}</span>
                        <span className="text-muted-foreground">=</span>
                        <code className="bg-muted/50 px-1 rounded text-[10px]">
                          {typeof p.value === 'string' ? p.value : JSON.stringify(p.value)}
                        </code>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">{p.source}</Badge>
                        <span className="text-muted-foreground">
                          Confidence: {(p.confidence * 100).toFixed(0)}%
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 text-red-500"
                          onClick={() => handleDeletePref(p.customer_id, p.category, p.preference_key)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
